import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Acl } from '../models/acl';
import { ImportReport } from '../models/import-report';
import { ListOptions } from '../models/list-options';
import { Organization } from '../models/organization';
import { Property } from '../models/property';
import { IimportableService } from './iimportable-service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class OrganizationsService extends ServiceBase implements IimportableService {
    constructor(private http: HttpClient) {
        super();
        this.currentOrganizationSubject = new BehaviorSubject<Organization>(JSON.parse(sessionStorage.getItem('currentOrganization')));
    }

    /**
     * Sujet pour indiquer si l'import est terminé
     */
    private importFinished = new Subject<ImportReport>();

    /**
     * Sujet pour indiquer l'organisation en cours
     */
    private currentOrganizationSubject: BehaviorSubject<Organization>;

    /**
     * Propriété pour indiquer si l'import est terminé
     */
    public get onImportFinished$() {
        return this.importFinished.asObservable();
    }

    /**
     * Propriété pour indiquer le changement de l'organization en cours
     */
    public get currentOrganizationChange$() {
        return this.currentOrganizationSubject.asObservable();
    }

    getOrganisation(organizationId: string, globalNotification = true, executeOption = false): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('global-notification', String(globalNotification))
                .set('X-OrganizationId', !executeOption ? '' : this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.get<Organization>(`${this.baseUrl}/api/organizations/${organizationId}`, httpOptions);
    }

    getOrganizationWithDisplayParentOrganization(organizationId: string): Observable<Organization> {
        return this.getOrganisation(organizationId).pipe(
            switchMap((organization: Organization) => {
                if (this.currentOrganizationSubject.getValue().id === organization.id) {
                    organization.displayParentOrganization = '';
                    return of(organization);
                } else {
                    return this.getOrganisation(organization.organizationId, false).pipe(
                        map((parentOrganization) => {
                            // Affecte le nom de l'organisation parente
                            organization.displayParentOrganization = parentOrganization.display;

                            return organization;
                        }),
                        catchError(() => {
                            console.log('erreur');
                            organization.displayParentOrganization = '';

                            // En cas d'erreur, organisation parente non trouvée, retourne l'organisation sans le nom de l'organisation parente
                            return of(organization);
                        })
                    );
                }
            })
        );
    }

    getOrganisations(executeOption: boolean = true, filter: string = null, listOptions: ListOptions = null): Observable<Organization[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', !executeOption ? '' : this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        if (listOptions) {
            httpOptions.headers = listOptions.addListOptions(httpOptions.headers);
        }

        const urlString = filter ? `${this.baseUrl}/api/organizations?filter=${encodeURIComponent(filter)}` : `${this.baseUrl}/api/organizations`;
        const organizations = this.http.get<Organization[]>(urlString, httpOptions);

        // Récupère le nom de l'organisation parente
        return organizations.pipe(
            map((orgs) => {
                orgs.forEach((org) => {
                    const displayParentOrg = orgs.find((x) => x.id === org.organizationId);
                    org.displayParentOrganization = !!displayParentOrg ? displayParentOrg.display : '';
                });

                return orgs;
            })
        );
    }

    addOrganization(organization: Organization): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.post<Organization>(`${this.baseUrl}/api/organizations`, organization, httpOptions);
    }

    addOrganizations(organizations: Organization[], globalNotification = true): Observable<ImportReport> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('global-notification', String(globalNotification))
                .set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.post<ImportReport>(`${this.baseUrl}/api/organizations/import`, organizations, httpOptions);
    }

    deleteOrganization(organizationId: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}`, httpOptions);
    }

    deleteOrganizationProperty(organizationId: string, propertyName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}/properties/${propertyName}`, httpOptions);
    }

    deleteOrganizationRight(organizationId: string, rightName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.delete(`${this.baseUrl}/api/organizations/${organizationId}/rights/${rightName}`, httpOptions);
    }

    updateOrganization(organization: Organization): Observable<Organization> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject?.value ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.patch<Organization>(`${this.baseUrl}/api/organizations/${organization.id}`, organization, httpOptions);
    }

    updateOrganizationProperty(organizationId: string, propertyName: string, propertyValue: string): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/organizations/${organizationId}/properties/${propertyName}`, JSON.stringify(propertyValue), httpOptions);
    }

    updateOrganizationRight(organizationId: string, rightName: string, rightValue: number): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('X-OrganizationId', this.currentOrganizationSubject ? this.currentOrganizationSubject.value.id : ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/organizations/${organizationId}/rights/${rightName}`, rightValue, httpOptions);
    }

    getCurrentOrganization(): Organization {
        return this.currentOrganizationSubject.value;
    }

    setCurrentOrganization(organization: Organization) {
        sessionStorage.setItem('currentOrganization', JSON.stringify(organization));
        this.currentOrganizationSubject.next(organization);
    }

    removeCurrentOrganization() {
        sessionStorage.removeItem('currentOrganization');
        this.currentOrganizationSubject.next(null);
    }

    import(datas: any, properties: any, rights: any) {
        let orgsToAdd: Organization[] = [];
        try {
            let datasToAdd = datas as Organization[];

            let propertiesOrgs: { organization: string; name: string; value: string }[]; //, readonly: any, legacyable: any}[];
            let rightsOrgs: { organization: string; name: string; value: number }[]; //, readonly: any, legacyable: any}[];

            if (properties) {
                propertiesOrgs = properties as { organization: string; name: string; value: string }[]; //, readonly: any, legacyable: any}[];
            }

            if (rights) {
                rightsOrgs = rights as { organization: string; name: string; value: number }[]; //, readonly: any, legacyable: any}[];
            }

            // Trie les organisations par arborescence pour les créer dans l'ordre
            datasToAdd = this.getTree(datasToAdd);

            datasToAdd.forEach((org) => {
                let newOrg: Organization = new Organization();

                // Force les valeurs en string au cas où c'est un nombre
                newOrg.organizationId = this.getCurrentOrganization().id; //org.organizationId ? String(org.organizationId) : '';
                newOrg.display = org.display ? String(org.display).trim() : '';
                newOrg.billing = org.billing ? String(org.billing).trim() : '';
                newOrg.appData = org.appData ? String(org.appData).trim() : '';

                if (propertiesOrgs) {
                    newOrg.properties = propertiesOrgs
                        .filter((p) => p.organization === org.display)
                        .map((a) => {
                            let property = new Property();
                            property.name = a.name.trim();
                            property.value = a.value ? String(a.value).trim() : '';
                            // property.readonly = BooleanConversion.convertToBoolean(a.readonly);
                            // property.legacyable = BooleanConversion.convertToBoolean(a.legacyable);

                            return property;
                        });
                }

                if (rightsOrgs) {
                    newOrg.rights = rightsOrgs
                        .filter((r) => r.organization === org.display)
                        .map((a) => {
                            let right = new Acl();
                            right.name = a.name.trim();
                            right.value = a.value;
                            // right.readonly =  BooleanConversion.convertToBoolean(a.readonly);
                            // right.legacyable =  BooleanConversion.convertToBoolean(a.legacyable);

                            return right;
                        });
                }

                orgsToAdd.push(newOrg);
            });

            this.addOrganizations(orgsToAdd, false).subscribe(
                (report) => {
                    this.importFinished.next(report);
                },
                () => {
                    const importError = new ImportReport();
                    importError.isImportError = true;
                    this.importFinished.next(importError);
                }
            );
        } catch (e) {
            const fileError = new ImportReport();
            fileError.isFileTreatmentError = true;
            this.importFinished.next(fileError);
        }
    }

    private getTree(organizations: Organization[]): Organization[] {
        // Copie de la liste des organisations
        const organizationsList = [...organizations];

        // Liste des organisations triés par arborescence
        const organizationsSorted: Organization[] = [];

        while (organizationsList.length > 0) {
            // Cherche une organization racine (organizationId ou seulement displayParentOrganization de renseigné)
            const rootOrganization = organizationsList.find((o) => o.organizationId || (!o.organizationId && !o.displayParentOrganization));

            // Cherche les organizations enfants
            this.searchChildren(organizationsList, organizationsSorted, rootOrganization);
        }

        return organizationsSorted;
    }

    /**
     * Recherche les enfants d'une organisation
     * @param organizationsList Liste des organisations
     * @param organizationsSorted Liste des organisations triées par arborescence
     * @param rootOrganization Organisation racine
     */
    private searchChildren(organizationsList: Organization[], organizationsSorted: Organization[], rootOrganization: Organization) {
        organizationsSorted.push(rootOrganization);

        // Supprime l'organisation racine
        organizationsList.splice(
            organizationsList.findIndex((o) => o.display === rootOrganization.display),
            1
        );

        // Recherche des organisations enfants
        organizationsList
            .filter((organization) => organization.displayParentOrganization === rootOrganization.display)
            .forEach((o) => {
                this.searchChildren(organizationsList, organizationsSorted, o);
            });
    }
}
